import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy,
  OnInit,
  inject,
  effect
} from '@angular/core';
import { NgxExtendedPdfViewerComponent, PDFNotificationService, pdfDefaultOptions } from 'ngx-extended-pdf-viewer';
import { UtilService } from '../../services/util.service';
import { AppService } from '../../shared/services/app.service';
import { HttpClient } from '@angular/common/http';

// Security: never run JavaScript embedded in a PDF (OpenAction / annotation / form actions).
// Library default is true, which lets an uploaded PDF call app.alert/confirm/prompt on our origin.
pdfDefaultOptions.enableScripting = false;

@Component({
  selector: 'ng2-pdfjs-viewer',
  templateUrl: './ng2-pdfjs-viewer.component.html',
  styleUrls: ['./ng2-pdfjs-viewer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class Ng2PdfjsViewerComponent implements OnInit, OnChanges {
  public _pdfSrc: any;
  @Input() set pdfSrc(src: any) {
    this._pdfSrc = src;
    if (this.innerViewer) {
      this.innerViewer.src = src; // triggers reload
    }
  }

  get pdfSrc() {
    return this._pdfSrc;
  }

  @Input() openFile = false;
  @Input() sidebar = true;
  @Input() download = true;
  @Input() print = true;
  @Input() downloadFileName?: string;

  @ViewChild(NgxExtendedPdfViewerComponent, { static: false }) innerViewer!: NgxExtendedPdfViewerComponent;

  public derivedName = 'document.pdf';

  private printInProgress = false;
  private printIframe: HTMLIFrameElement | null = null;

  constructor(
    public utilService: UtilService,
    public appService: AppService,
    public http: HttpClient
  ) {
    const notificationService = inject(PDFNotificationService);

    effect(() => {
      if (this.appService.checkNativeApp()) {
        const PDFViewerApplication: any = notificationService.onPDFJSInitSignal();
        if (PDFViewerApplication) {
          PDFViewerApplication.downloadManager.download = (data: any, type: string, filename: string) => {
            this.downloadFile(data, filename);
          };
        }
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['pdfSrc']) {
      this.derivedName = this._deriveFileName(this.pdfSrc?.toString?.() || '');
    }
  }

  ngOnInit() { }

  private _deriveFileName(url: string): string {
    try {
      const parts = url.split('/');
      let name = parts[parts.length - 1].split('?')[0] || 'document.pdf';

      if (!name.toLowerCase().endsWith('.pdf')) {
        name += '.pdf';
      }

      return name;
    } catch {
      return 'document.pdf';
    }
  }

  refresh() { }

  checkmobile() {
    return this.utilService.isMobile() && !this.appService.checkNativeApp();
  }

  printFile() {
    if (this._pdfSrc instanceof Blob) {
      const blobUrl = URL.createObjectURL(this._pdfSrc);
      window.open(blobUrl, '_blank');
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } else if (typeof this._pdfSrc === 'string' && this._pdfSrc.startsWith('blob:')) {
      window.open(this._pdfSrc, '_blank');
    } else {
      window.open(this._pdfSrc, '_blank');
    }
  }

  async downloadFile(fileData: any, fileName: string) {
    if (this.appService.checkNativeApp()) {
      try {
        const mimeType = 'application/pdf';
        const resolvedFileName = this.downloadFileName || this.derivedName;

        if (this.utilService.isiOS()) {
          let blob: Blob;

          if (fileData instanceof Blob) {
            blob = fileData;
          } else if (fileData instanceof Uint8Array || fileData instanceof ArrayBuffer) {
            blob = new Blob([fileData], { type: mimeType });
          } else if (typeof fileData === 'string') {
            const res = await fetch(fileData);
            blob = await res.blob();
          } else {
            const res = await fetch(this._pdfSrc);
            blob = await res.blob();
          }

          this.utilService.nativeFileHandling(blob, mimeType, resolvedFileName);
        } else {
          if (this._pdfSrc instanceof Blob) {
            this.utilService.nativeFileHandling(this._pdfSrc, mimeType, resolvedFileName);
          } else if (typeof this._pdfSrc === 'string' && this._pdfSrc.startsWith('blob:')) {
            const res = await fetch(this._pdfSrc);
            const blob = await res.blob();
            this.utilService.nativeFileHandling(blob, mimeType, resolvedFileName);
          } else if (typeof this._pdfSrc === 'string') {
            const res = await fetch(this._pdfSrc);
            const blob = await res.blob();
            this.utilService.nativeFileHandling(blob, mimeType, resolvedFileName);
          } else {
            console.error('Unsupported _pdfSrc type:', this._pdfSrc);
          }
        }
      } catch (error) {
        console.error('Download failed:', error);
        this.handleDownloadError(error);
      }
    }
  }

  private handleDownloadError(error: any): void {
    console.error('Download error:', error);
  }
}